import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import * as LDAP from 'src/config/LDAP';
import { ACTIVE, INACTIVE } from 'src/constants/EntityStatus';
import { useAppSelector } from 'src/store/store';
import { setSiteCapacityFormData } from 'src/store/updateSiteCapacitySlice/updateSiteCapacitySlice';
import { JmrJobTypes } from 'src/utils/jmrUtils';
import { createSearchRegExp } from 'src/utils/searchUtils';

import ShiftList from '../shiftList/shiftList';

const SiteCapacityFormFields = () => {
  const dispatch = useDispatch();
  const { ldap } = useAppSelector((state) => state.user);
  const { siteCapacityFormData, shifts } = useAppSelector((state) => state.updateSiteCapacityPage);
  const [shiftsDropdownSearchQuery, setShiftsDropdownSearchQuery] = useState();
  const searchRegExp = createSearchRegExp(shiftsDropdownSearchQuery);
  const matchedShiftsDropdownValues = shifts
    .map((shift) => ({ label: shift.shiftCode, value: shift.shiftCode }))
    .filter((option) => !shiftsDropdownSearchQuery || searchRegExp.test(option.label));

  // initial Active/Inactive state, before any in-session toggling
  const [cachedActiveStatus, _] = useState(siteCapacityFormData?.active);

  const isSiteJobActive = siteCapacityFormData?.active === ACTIVE;

  const isAdmin = ldap.includes(LDAP.ADMIN);
  // non-admins can activate a TLD role but cannot deactivate it once active
  const restrictTldDeactivation = siteCapacityFormData?.jobType === JmrJobTypes.TLD && !isAdmin;

  useEffect(() => {
    const shiftNames = siteCapacityFormData?.shiftNames || [];
    const myShifts = siteCapacityFormData?.shifts || [];
    if (shiftNames.length !== myShifts.length) {
      dispatch(
        setSiteCapacityFormData({
          ...siteCapacityFormData,
          shifts: shiftNames.map((shiftCode) => {
            const existing = myShifts.filter((s) => s?.shiftCode === shiftCode);
            return existing.length > 0 ? existing[0] : shifts.filter((s) => s.shiftCode === shiftCode)[0];
          }),
        }),
      );
    }
  }, [siteCapacityFormData?.shiftNames]);

  const setIsActive = (active: boolean) => {
    dispatch(
      setSiteCapacityFormData({
        ...siteCapacityFormData,
        active: active ? ACTIVE : INACTIVE,
      }),
    );
  };

  return (
    <Column spacing="400" alignmentVertical="top">
      {(!restrictTldDeactivation || cachedActiveStatus !== ACTIVE) && (
        <Row>
          <Toggle checked={isSiteJobActive} onChange={setIsActive} />
          <Text type="b300">Activated</Text>
        </Row>
      )}
      {restrictTldDeactivation && cachedActiveStatus === INACTIVE && isSiteJobActive && (
        <Text type="b200" color="error">
          Once saved, this TLD role cannot be deactivated without admin support.
        </Text>
      )}
      {cachedActiveStatus === ACTIVE && !isSiteJobActive && (
        <Alert type="error" title="WARNING: Job Deactivation Policy">
          Maintaining artificially small role profiles (deactivating possible roles to limit accommodation options) is
          against policy and will result in escalation.
        </Alert>
      )}
      <Column spacing="200">
        <Text type="b300">Available Shifts</Text>
        <Select
          disabled={!isSiteJobActive}
          searchQuery={shiftsDropdownSearchQuery}
          onSearch={setShiftsDropdownSearchQuery}
          placeholder="Add shifts available for the job"
          value={siteCapacityFormData?.shiftNames}
          onChange={(shiftNames) => {
            dispatch(setSiteCapacityFormData({ ...siteCapacityFormData, shiftNames }));
          }}
        >
          {matchedShiftsDropdownValues.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
          {!matchedShiftsDropdownValues.length ? (
            <Column alignmentVertical="center" spacing="300" spacingInset="500">
              <Text alignment="center">No results</Text>
            </Column>
          ) : null}
        </Select>
      </Column>
      <ShiftList isSiteJobActive={isSiteJobActive} />
    </Column>
  );
};

export default SiteCapacityFormFields;
